/**
 * BoltConversationItem — single row in the BOLT conversation history list.
 */

import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Trash2, Pencil } from 'lucide-react';

function formatRelativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function BoltConversationItem({
  conversation,
  isActive,
  onSelect,
  onDelete,
  onRename,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const convId = conversation.conversationId || conversation._id;
  const title =
    conversation.title ||
    (conversation.lastMessage && conversation.lastMessage.slice(0, 48)) ||
    'Untitled';
  const when = formatRelativeTime(conversation.lastActiveAt || conversation.createdAt);
  const count = conversation.messageCount;

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  return (
    <div
      className={`bolt-conversation-item ${isActive ? 'bolt-conversation-item--active' : ''}`}
    >
      <button
        type="button"
        className="bolt-conversation-item__main"
        onClick={() => onSelect?.(convId)}
      >
        <span className="bolt-conversation-item__title">{title}</span>
        <span className="bolt-conversation-item__meta">
          {when}
          {typeof count === 'number' ? (
            <span className="bolt-conversation-item__badge">{count}</span>
          ) : null}
        </span>
      </button>
      <div className="bolt-conversation-item__menu-wrap" ref={menuRef}>
        <button
          type="button"
          className="bolt-conversation-item__kebab"
          aria-label="Conversation actions"
          aria-expanded={menuOpen}
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((o) => !o);
          }}
        >
          <MoreVertical size={16} />
        </button>
        {menuOpen && (
          <div className="bolt-conversation-item__menu" role="menu">
            <button
              type="button"
              role="menuitem"
              className="bolt-conversation-item__menu-btn"
              onClick={() => {
                setMenuOpen(false);
                onRename?.(convId, conversation.title || title);
              }}
            >
              <Pencil size={14} /> Rename
            </button>
            <button
              type="button"
              role="menuitem"
              className="bolt-conversation-item__menu-btn bolt-conversation-item__menu-btn--danger"
              onClick={() => {
                setMenuOpen(false);
                onDelete?.(convId);
              }}
            >
              <Trash2 size={14} /> Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
